import React from 'react'
import { Box, Typography } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useMutation } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import GenericModal from '../../../components/GenericModal'
import GenericDateTimePicker from '../../../components/GenericDateTimePicker'
import DefaultModalActions from '../../../components/GenericModal/DefaultModalActions'
import { notification } from '../../../components/Notifications'
import { notifyError } from '../../../utils/notifyError'
import { useAppDispatch } from '../../../store'
import { getSlotSectionApi } from '../../../store/slices/distributionPlanSlice/apis'
import { DistributionManager } from '../../../APIManager'
import { type ISlot } from '../../../models/DistributionPlan'
import { queryClient } from '../../../components/Providers/ReactQueryProvider'
import { useSlotTaskContext } from '../../../contexts/SlotTaskContext'
import { HttpStatusCode } from '../../../consts/httpStatusCode'

interface SurveyProjectReopenModalProps {
  slot: ISlot
  isOpen: boolean
  onClose: () => void
}

interface ReopenSurveyProjectPayload {
  endTimestamp: string | Date
  releaseTimestamp: string | Date
}

interface FormValues {
  endTimestamp: Date | string
  releaseTimestamp: Date | string
}

const validationSchema: yup.ObjectSchema<FormValues> = yup.object({
  endTimestamp: yup
    .date()
    .typeError('End timestamp is required')
    .required('End timestamp is required')
    .nullable()
    .test('end-timestamp-future', 'End timestamp must be in the future', (value) => {
      if (!value) return false
      return new Date(value).getTime() > Date.now()
    }),
  releaseTimestamp: yup
    .date()
    .typeError('New Results Release Date  is required')
    .required('New Results Release Date  is required')
    .nullable()
    .test('release-timestamp-after-end', 'New Results Release Date  must be after end timestamp', function (value) {
      const { endTimestamp } = this.parent as FormValues
      if (!value || !endTimestamp) return false
      return new Date(value).getTime() > new Date(endTimestamp).getTime()
    })
    .required('New Results Release Date  is required')
}) as yup.ObjectSchema<FormValues>

const toIsoString = (value: Date | string): string => (value instanceof Date ? value.toISOString() : new Date(value).toISOString())

const SurveyProjectReopenModal: React.FC<SurveyProjectReopenModalProps> = ({ slot, isOpen, onClose }) => {
  const dispatch = useAppDispatch()
  const { id } = useParams()
  const params = new URLSearchParams(window.location.search)

  const methods = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      endTimestamp: slot.slotEndTimestamp,
      releaseTimestamp: slot.releaseTimestamp
    }
  })

  const { handleSubmit } = methods

  const { setTask } = useSlotTaskContext()

  const { mutate: reopenSurveyProject, isPending: isReopeningSurveyProject } = useMutation({
    mutationFn: async (payload: ReopenSurveyProjectPayload) => {
      return await DistributionManager.reopenSurveyProject(slot.slotId, {
        endTimestamp: payload.endTimestamp,
        releaseTimestamp: payload.releaseTimestamp
      })
    },
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: ['slotDataById', slot.slotId] })
      const { taskId, message } = response?.data ?? {}
      if (response?.status === HttpStatusCode.ACCEPTED && taskId) {
        setTask(taskId, message ?? 'Course sections are being re-synced in the background.', 'Course sections have finished re-syncing.')
      }
      notification(message ?? 'Survey project reopened successfully', { variant: 'success' })
      void dispatch(getSlotSectionApi({ urlId: id ?? slot?.slotId, params }))
      onClose()
    },
    onError: (error: unknown) => {
      notifyError(error, { fallbackMessage: 'Failed to reopen survey project. Please try again.' })
    }
  })

  const handleFormSubmit = (values: FormValues): void => {
    // yup schema guarantees both fields are non-null Date objects at this point
    reopenSurveyProject({
      endTimestamp: toIsoString(values.endTimestamp),
      releaseTimestamp: toIsoString(values.releaseTimestamp)
    })
  }

  return (
      <GenericModal
        isOpen={isOpen}
        onClose={onClose}
        title='Re-open Survey Project?'
        maxWidth='sm'
        action={false}
      >
        <FormProvider {...methods}>
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <Box display='flex' flexDirection='column' gap={4}>
              <Box>
                <Typography fontSize='14px' color='text.secondary'>
                  Re-opening this survey project may require pushing back the Aggregate Results Release Date. While the project is reopened, admins will temporarily lose access to academic unit-level results, but course section-level results will still be available.
                </Typography>
              </Box>
              <GenericDateTimePicker
                name='endTimestamp'
                label='New End Date'
                required
                minDate={new Date()}
              />
              <GenericDateTimePicker
                name='releaseTimestamp'
                label='New Results Release Date'
                required
                minDate={new Date()}
              />
            </Box>
            <DefaultModalActions
              confirmButtonProps={{
                text: 'Save',
                type: 'submit',
                loading: isReopeningSurveyProject
              }}
              destructiveButtonProps={{
                onClick: onClose
              }}
            />
          </form>
        </FormProvider>
      </GenericModal>
  )
}

export default SurveyProjectReopenModal
